import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { fetchAuthSession } from '../auth/gateway';
import { API_CONFIG } from '../config/api';
import { secureLogger } from '../utils/secureLogger';

const SELECTED_DEVICE_STORAGE_KEY = 'selectedDeviceId';

interface Device {
  id: string;
  device_id: string;
  imei: string;
  name: string;
  device_type: string;
  tags?: string[];
  operation_mode: 'batch' | 'realtime';
  last_seen_at?: string;
  created_at: string;
}

interface DeviceContextType {
  devices: Device[];
  selectedDevice: Device | null;
  setSelectedDevice: (device: Device | null) => void;
  loadDevices: () => Promise<void>;
  loading: boolean;
  updateDeviceMode: (deviceId: string, mode: 'batch' | 'realtime') => void;
}

const DeviceContext = createContext<DeviceContextType | undefined>(undefined);

export const useDevice = () => {
  const context = useContext(DeviceContext);
  if (!context) {
    throw new Error('useDevice must be used within a DeviceProvider');
  }
  return context;
};

interface DeviceProviderProps {
  children: ReactNode;
  userId?: string;
}

export const DeviceProvider: React.FC<DeviceProviderProps> = ({ children, userId }) => {
  const [devices, setDevices] = useState<Device[]>([]);
  const [selectedDevice, setSelectedDevice] = useState<Device | null>(null);
  const [loading, setLoading] = useState(false);

  const sortDevicesForDefaultSelection = (deviceList: Device[]) => {
    return [...deviceList].sort((a, b) => {
      const lastSeenDiff =
        new Date(b.last_seen_at || 0).getTime() - new Date(a.last_seen_at || 0).getTime();
      if (lastSeenDiff !== 0) {
        return lastSeenDiff;
      }
      return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
    });
  };

  const loadDevices = async () => {
    setLoading(true);
    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.accessToken?.toString();
      
      if (!token) {
        secureLogger.error('No valid token found for device loading');
        return;
      }
      
      const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.DEVICES}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        const deviceList = data.devices || [];
        setDevices(deviceList);

        const persistedSelectedDeviceId = localStorage.getItem(SELECTED_DEVICE_STORAGE_KEY);

        // 自動選択: 現在選択中 → 永続化済み → 最終利用端末 → 作成日時の新しい端末
        if (deviceList.length > 0) {
          const currentSelected = selectedDevice;
          const stillExists = currentSelected
            ? deviceList.find((d: Device) => d.id === currentSelected.id)
            : null;
          const persistedSelected = persistedSelectedDeviceId
            ? deviceList.find((d: Device) => d.id === persistedSelectedDeviceId)
            : null;

          if (stillExists) {
            setSelectedDevice(stillExists);
          } else if (persistedSelected) {
            setSelectedDevice(persistedSelected);
          } else {
            const [defaultDevice] = sortDevicesForDefaultSelection(deviceList);
            setSelectedDevice(defaultDevice ?? null);
          }
        } else {
          setSelectedDevice(null);
          localStorage.removeItem(SELECTED_DEVICE_STORAGE_KEY);
        }
      }
    } catch (error) {
      secureLogger.error('Failed to load devices', { error: 'Device loading failed' });
    } finally {
      setLoading(false);
    }
  };

  const updateDeviceMode = (deviceId: string, mode: 'batch' | 'realtime') => {
    setDevices(prev => 
      prev.map(device => 
        device.id === deviceId 
          ? { ...device, operation_mode: mode }
          : device
      )
    );
    
    if (selectedDevice?.id === deviceId) {
      setSelectedDevice(prev => 
        prev ? { ...prev, operation_mode: mode } : null
      );
    }
  };

  useEffect(() => {
    if (userId) {
      loadDevices();
    }
  }, [userId]);

  useEffect(() => {
    if (selectedDevice?.id) {
      localStorage.setItem(SELECTED_DEVICE_STORAGE_KEY, selectedDevice.id);
      return;
    }
    localStorage.removeItem(SELECTED_DEVICE_STORAGE_KEY);
  }, [selectedDevice]);

  return (
    <DeviceContext.Provider value={{
      devices,
      selectedDevice,
      setSelectedDevice,
      loadDevices,
      loading,
      updateDeviceMode
    }}>
      {children}
    </DeviceContext.Provider>
  );
};
